import * as React from "react";
import PropTypes from 'prop-types';
import { format, isSameDay, parseISO } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Clock, User, Phone, AlertCircle, RefreshCw } from "lucide-react";
import { formatRupees } from "../utils/currency";

// Helper to normalize dates for grouping
const normalizeDate = (dateOrStr) => {
    if (!dateOrStr) return "";
    try {
        const d = typeof dateOrStr === 'string' ? parseISO(dateOrStr) : dateOrStr;
        return format(d, "yyyy-MM-dd");
    } catch {
        return "";
    }
};

// Extracted Day component for stability
const CalendarDay = ({ dayDate, date, appointmentsByDate, setDate, displayMonth, ...dayProps }) => {
    const dateStr = normalizeDate(dayDate);
    const dayAppointments = appointmentsByDate[dateStr] || [];
    const count = dayAppointments.length;
    const isSelected = isSameDay(dayDate, date);
    const isToday = isSameDay(dayDate, new Date());

    if (dayProps.hidden) return <div className="h-10 sm:h-12 w-[14.28%]" />;

    return (
        <div className="h-full w-full flex items-center justify-center relative">
            <button
                {...dayProps}
                type="button"
                onClick={(e) => {
                    if (dayProps.onClick) dayProps.onClick(e);
                    setDate(dayDate);
                }}
                className={cn(
                    "h-9 w-9 sm:h-10 sm:w-10 transition-all duration-200 rounded-lg flex flex-col items-center justify-center relative",
                    isSelected ? "bg-primary text-primary-foreground shadow-md scale-105" : "hover:bg-accent",
                    isToday && !isSelected && "border-2 border-primary/30 text-primary font-bold",
                    dayProps.disabled && "opacity-50 cursor-not-allowed",
                    !displayMonth && "text-muted-foreground opacity-50"
                )}
            >
                <span className="text-xs sm:text-sm font-medium">{format(dayDate, "d")}</span>
                {count > 0 && (
                    <div className={cn(
                        "absolute -top-1 -right-1 h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full flex items-center justify-center text-[7px] sm:text-[9px] font-bold shadow-sm border border-white",
                        isSelected ? "bg-white text-primary" : "bg-slate-900 text-white"
                    )}>
                        {count}
                    </div>
                )}
            </button>
        </div>
    );
};

CalendarDay.propTypes = {
    dayDate: PropTypes.instanceOf(Date),
    date: PropTypes.instanceOf(Date),
    appointmentsByDate: PropTypes.object,
    setDate: PropTypes.func,
    displayMonth: PropTypes.bool,
    hidden: PropTypes.bool,
    onClick: PropTypes.func,
    disabled: PropTypes.bool
};

export function CalendarView({ appointments, allPatients, allDoctors, clinics, onAppointmentClick, onStatusUpdate, selectedDate, setSelectedDate, getAppointmentAccess }) {
    // If no external state provided (backward compatibility), use local state
    const [localDate, setLocalDate] = React.useState(new Date());
    const date = selectedDate || localDate;
    const setDate = setSelectedDate || setLocalDate;

    // Group appointments by date for indicators
    const appointmentsByDate = React.useMemo(() => {
        const groups = {};
        if (!appointments || !Array.isArray(appointments)) return groups;

        appointments.forEach((apt) => {
            if (!apt.date_time) return;
            try {
                const dateObj = parseISO(apt.date_time);
                const d = format(dateObj, "yyyy-MM-dd");
                if (!groups[d]) groups[d] = [];
                groups[d].push(apt);
            } catch (err) {
                console.error("Invalid date for appointment:", apt.date_time, err);
            }
        });
        return groups;
    }, [appointments]);

    const selectedDateStr = normalizeDate(date);
    const selectedDayAppointments = appointmentsByDate[selectedDateStr] || [];

    return (
        <div className="flex flex-col md:flex-row gap-6 min-h-[450px] animate-in fade-in duration-500">
            <div className="w-full md:w-[380px] shrink-0 flex flex-col">
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    className="rounded-xl border shadow-sm bg-white p-2 sm:p-4 w-full"
                    classNames={{
                        months: "w-full",
                        month: "w-full space-y-4",
                        table: "w-full border-collapse table-fixed !min-w-0",
                        head_row: "flex w-full",
                        row: "flex w-full mt-1",
                        head_cell: "text-muted-foreground rounded-md font-medium text-[10px] sm:text-[0.8rem] w-[14.28%] h-10 flex items-center justify-center !p-0",
                        cell: "relative !p-0 text-center text-sm focus-within:relative focus-within:z-20 w-[14.28%] h-10 sm:h-12 flex items-center justify-center",
                        day: cn(
                            "h-9 w-9 sm:h-11 sm:w-11 p-0 font-normal aria-selected:opacity-100 flex items-center justify-center"
                        ),
                    }}
                    components={{
                        Day: (props) => (
                            <CalendarDay
                                {...props}
                                dayDate={props.date} // eslint-disable-line react/prop-types
                                date={date}
                                setDate={setDate}
                                appointmentsByDate={appointmentsByDate}
                            />
                        ),
                    }}
                />
            </div>

            <div className="flex-1 flex flex-col min-w-0 max-w-full overflow-hidden">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-800">
                        {format(date, "MMMM d, yyyy")}
                    </h2>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {selectedDayAppointments.length} Appointment{selectedDayAppointments.length !== 1 ? 's' : ''}
                    </Badge>
                </div>

                <ScrollArea className="h-[400px] md:h-full pr-4">
                    {selectedDayAppointments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-400 border-2 border-dashed rounded-xl">
                            <Clock className="w-8 h-8 mb-2 opacity-20" />
                            <p>No appointments scheduled</p>
                        </div>
                    ) : (
                        <div className="space-y-3 pb-6">
                            {[...selectedDayAppointments].sort((a, b) => new Date(a.date_time) - new Date(b.date_time)).map((apt) => {
                                const patient = allPatients.find(p => String(p.id) === String(apt.patient_id));
                                const isAnonymized = apt.is_anonymized;
                                const doctor = allDoctors && apt.doctor_id ? allDoctors.find(d => d.id === apt.doctor_id) : null;
                                const doctorName = doctor ? (doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`) : 'Other Doctor';

                                // Determine context label (Clinic vs Personal)
                                const clinic = clinics && apt.clinic_id ? clinics.find(c => c.id === apt.clinic_id) : null;
                                const contextLabel = clinic ? clinic.name : (apt.clinic_id === 'personal' || !apt.clinic_id ? 'Personal' : 'Unknown Context');
                                const isMyAppointment = localStorage.getItem('user_id') === apt.doctor_id;

                                const access = getAppointmentAccess ? getAppointmentAccess(apt) : { canEdit: true, canViewFee: true };

                                return (
                                    <div
                                        key={apt.id}
                                        onClick={() => {
                                            if (isAnonymized) return;
                                            onAppointmentClick(apt.id);
                                        }}
                                        className={cn(
                                            "p-4 bg-white border rounded-xl hover:shadow-md transition-all group",
                                            isAnonymized ? "opacity-75 bg-slate-50 cursor-default" : "cursor-pointer active:scale-[0.98]"
                                        )}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-full flex items-center justify-center font-bold",
                                                    isAnonymized ? "bg-slate-200 text-slate-500" : "bg-blue-50 text-blue-600"
                                                )}>
                                                    {isAnonymized ? <User size={20} /> : (patient?.name?.charAt(0) || "?")}
                                                </div>
                                                <div>
                                                    <h3 className={cn(
                                                        "font-semibold transition-colors",
                                                        isAnonymized ? "text-slate-500 italic" : "text-slate-900 group-hover:text-blue-600"
                                                    )}>
                                                        {isAnonymized
                                                            ? `Busy - ${doctorName}`
                                                            : (patient?.name || `Unknown (${apt.patient_id?.substring(0, 6)}...)`)
                                                        }
                                                    </h3>
                                                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium flex items-center gap-1">
                                                        {apt.status}
                                                        {apt.sync_status === 'failed' && (
                                                            <span title={apt.sync_error || 'Sync failed'} className="text-red-500">
                                                                <AlertCircle size={12} />
                                                            </span>
                                                        )}
                                                        {apt._synced === false && apt.sync_status !== 'failed' && (
                                                            <span title="Syncing..." className="text-amber-500">
                                                                <RefreshCw size={12} className="animate-spin" />
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge className={cn(
                                                "capitalize ml-2",
                                                apt.status === "scheduled" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" :
                                                    apt.status === "completed" ? "bg-green-100 text-green-700 hover:bg-green-100" :
                                                        "bg-red-100 text-red-700 hover:bg-red-100"
                                            )}>
                                                {format(parseISO(apt.date_time), "h:mm a")}
                                            </Badge>
                                        </div>

                                        {/* Context & Doctor Badges */}
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            <span className={cn(
                                                "text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider",
                                                !clinic ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-orange-50 text-orange-700 border-orange-200"
                                            )}>
                                                {contextLabel}
                                            </span>
                                            {!isMyAppointment && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-slate-100 text-slate-600 border-slate-200">
                                                    {doctorName}
                                                </span>
                                            )}
                                        </div>

                                        {!isAnonymized && (
                                            <div className="flex justify-between items-center text-sm text-slate-600 mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Phone size={14} className="text-slate-400" />
                                                    <span>{patient?.phone || "No phone"}</span>
                                                </div>
                                                {access.canViewFee && (
                                                    <div className="flex items-center gap-1 font-medium">
                                                        <span className="text-slate-900">{formatRupees(apt.fee_amount)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!isAnonymized && access.canEdit && apt.status === "scheduled" && onStatusUpdate && (
                                            <div className="flex gap-2 pt-3 border-t">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 h-8 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onStatusUpdate(apt.id, "completed");
                                                    }}
                                                >
                                                    Complete
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="flex-1 h-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onStatusUpdate(apt.id, "cancelled");
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}

                                        {apt.notes && !isAnonymized && (
                                            <div className="mt-2 text-xs text-slate-400 italic line-clamp-1 border-t pt-2">
                                                {apt.notes}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
            </div>
        </div>
    );
}

CalendarView.propTypes = {
    appointments: PropTypes.array,
    allPatients: PropTypes.array,
    allDoctors: PropTypes.array,
    clinics: PropTypes.array,
    onAppointmentClick: PropTypes.func,
    onStatusUpdate: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    setSelectedDate: PropTypes.func,
    getAppointmentAccess: PropTypes.func
};
